import React from "react";
import { Link } from "react-router-dom";
import { BIZ } from "@/data/site";
import { Phone, Mail, MapPin, MessageSquare, ShieldCheck } from "lucide-react";
import BBBBadge from "@/components/BBBBadge";

/**
 * Full marketing footer. Includes contact info, service areas, and links to
 * every legal / secondary page so search engines and customers can find them.
 */
export default function SiteFooter() {
  return (
    <footer className="bg-stone-900 text-stone-400 pt-16 pb-24 md:pb-12" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-8">
        {/* Brand */}
        <div className="md:col-span-1">
          <div className="rounded-2xl overflow-hidden bg-white p-2 shadow-sm inline-block">
            <img src="/wynridge-logo.png?v=2" alt={BIZ.name} className="h-14 w-auto object-contain" />
          </div>
          <p className="mt-4 text-sm text-stone-400 leading-relaxed">
            Local, licensed &amp; insured pressure washing and soft washing serving {BIZ.region}.
          </p>
          <a
            href={BIZ.gbpUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-block text-xs font-semibold text-electric-300 hover:text-electric-400"
          >
            See us on Google →
          </a>

          <div className="mt-6 flex items-center gap-3" data-testid="footer-trust-row">
            <div className="inline-flex items-center gap-2 rounded-lg bg-white/5 border border-white/10 px-3 h-14">
              <ShieldCheck className="w-5 h-5 text-electric-300" />
              <div className="leading-tight text-[11px] font-bold uppercase tracking-wider">
                Licensed<br />&amp; Insured
              </div>
            </div>
            <BBBBadge size="md" onDark />
          </div>
        </div>

        {/* Contact */}
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-electric-300 mb-4">Contact</div>
          <ul className="space-y-3 text-sm">
            <li>
              <a href={`tel:${BIZ.phoneTel}`} className="flex items-center gap-2 hover:text-white">
                <Phone className="w-4 h-4 text-electric-400" /> {BIZ.phone}
              </a>
            </li>
            <li>
              <a href={BIZ.phoneSmsHref} className="flex items-center gap-2 hover:text-white">
                <MessageSquare className="w-4 h-4 text-electric-400" /> Text us
              </a>
            </li>
            <li>
              <a href={`mailto:${BIZ.email}`} className="flex items-center gap-2 hover:text-white break-all">
                <Mail className="w-4 h-4 text-electric-400 flex-shrink-0" /> {BIZ.email}
              </a>
            </li>
            <li className="flex items-start gap-2 text-stone-400">
              <MapPin className="w-4 h-4 text-electric-400 flex-shrink-0 mt-0.5" />
              <span>{BIZ.city}, {BIZ.stateShort} {BIZ.zip}<br /><span className="text-xs text-stone-500">Service-area business — no walk-in location</span></span>
            </li>
          </ul>
        </div>

        {/* Services */}
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-electric-300 mb-4">Services</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/pressure-washing-estimate-calculator/" className="hover:text-white">Instant estimate</Link></li>
            <li><Link to="/house-washing-goodlettsville-tn/" className="hover:text-white">House washing</Link></li>
            <li><Link to="/roof-cleaning-goodlettsville-tn/" className="hover:text-white">Roof cleaning</Link></li>
            <li><Link to="/driveway-cleaning-goodlettsville-tn/" className="hover:text-white">Driveway cleaning</Link></li>
            <li><Link to="/gutter-cleaning-goodlettsville-tn/" className="hover:text-white">Gutter cleaning</Link></li>
            <li><Link to="/window-cleaning-goodlettsville-tn/" className="hover:text-white">Window cleaning</Link></li>
            <li><Link to="/commercial-pressure-washing-goodlettsville-tn/" className="hover:text-white">Commercial</Link></li>
            <li><Link to="/pressure-washing-services/" className="hover:text-white font-semibold">All services →</Link></li>
          </ul>
        </div>

        {/* Company */}
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-electric-300 mb-4">Company</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about/" className="hover:text-white">About</Link></li>
            <li><Link to="/pressure-washing-gallery/" className="hover:text-white">Gallery</Link></li>
            <li><Link to="/reviews/" className="hover:text-white">Reviews</Link></li>
            <li><Link to="/service-areas/" className="hover:text-white">Service areas</Link></li>
            <li><Link to="/faq/" className="hover:text-white">FAQ</Link></li>
            <li><Link to="/contact/" className="hover:text-white">Contact</Link></li>
            <li><Link to="/privacy-policy/" className="hover:text-white text-xs">Privacy</Link></li>
            <li><Link to="/terms-and-estimate-disclaimer/" className="hover:text-white text-xs">Terms</Link></li>
          </ul>
        </div>
      </div>

      <div className="mt-12 pt-8 border-t border-stone-800 max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="text-xs text-stone-500">
          © {new Date().getFullYear()} {BIZ.name} · {BIZ.city}, {BIZ.stateShort} {BIZ.zip} · Licensed &amp; Insured
        </div>
        <Link
          to="/login"
          className="text-[10px] text-stone-600 hover:text-stone-400 uppercase tracking-widest"
          data-testid="footer-admin-login"
        >
          Admin
        </Link>
      </div>
      {/* Mobile spacer so page content clears the fixed bottom app-bar */}
      <div className="md:hidden h-20" aria-hidden="true" />
    </footer>
  );
}
